/**
 * Utilidades para manejo de fechas en componentes DateField
 * Funciones puras para validación, parseo y formateo
 */

export interface DateValidationOptions {
  minYear: number;
  maxYear: number;
  maxDate?: string; // formato "YYYY-MM-DD", opcional
}

/**
 * Verifica si un año es bisiesto
 */
export const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

/**
 * Obtiene el número de días en un mes específico
 */
export const getDaysInMonth = (month: number, year: number): number => {
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  
  return daysInMonth[month - 1] || 31;
};

/**
 * Parsea una cadena de fecha en sus componentes
 * Soporta formatos: "DD/MM/YYYY", "MM/YYYY", "D/M/YYYY", etc.
 * También soporta separadores: "/", "-", "."
 */
export const parseDateString = (str: string): { day?: number; month?: number; year?: number } => {
  if (!str || typeof str !== 'string') {
    return {};
  }

  // Normalizar separadores a "/"
  const normalized = str.replace(/[-.]/g, '/');
  const parts = normalized.split('/').filter(part => part.trim() !== '');

  if (parts.length === 0) {
    return {};
  }

  // Convertir a números, filtrar valores inválidos
  const numbers = parts.map(part => {
    const num = parseInt(part.replace(/\D/g, ''), 10);
    return isNaN(num) ? undefined : num;
  }).filter(num => num !== undefined) as number[];

  if (numbers.length === 0) {
    return {};
  }

  // Determinar formato basado en cantidad de partes
  if (numbers.length === 1) {
    // Solo un número - podría ser día, mes o año
    const num = numbers[0];
    if (num > 31) {
      return { year: num };
    } else if (num > 12) {
      return { day: num };
    } else {
      return { month: num };
    }
  } else if (numbers.length === 2) {
    // Dos números - formato MM/YYYY
    const [first, second] = numbers;
    if (second > 31) {
      // Segundo número es año
      return { month: first, year: second };
    } else {
      // Asumir DD/MM
      return { day: first, month: second };
    }
  } else if (numbers.length >= 3) {
    // Tres o más números - formato DD/MM/YYYY
    const [day, month, year] = numbers;
    return { day, month, year };
  }

  return {};
};

/**
 * Valida si un día es válido considerando mes, año y restricciones
 */
export const isValidDay = (
  day: number,
  month: number,
  year: number,
  options: DateValidationOptions
): boolean => {
  // Validaciones básicas
  if (day < 1 || month < 1 || month > 12) {
    return false;
  }

  // Validar rango de año
  if (year < options.minYear || year > options.maxYear) {
    return false;
  }

  // Validar día según el mes
  const maxDaysInMonth = getDaysInMonth(month, year);
  if (day > maxDaysInMonth) {
    return false;
  }

  // Validar contra maxDate si está definida
  if (options.maxDate) {
    const maxDateObj = new Date(options.maxDate + 'T23:59:59.999Z');
    const currentDate = new Date(year, month - 1, day);

    if (currentDate > maxDateObj) {
      return false;
    }
  }

  return true;
};

/**
 * Valida si un mes/año es válido considerando las restricciones
 */
export const isValidMonth = (
  month: number,
  year: number,
  options: DateValidationOptions
): boolean => {
  // Validaciones básicas
  if (month < 1 || month > 12) {
    return false;
  }

  // Validar rango de año
  if (year < options.minYear || year > options.maxYear) {
    return false;
  }

  // Validar contra maxDate si está definida
  if (options.maxDate) {
    const maxDateObj = new Date(options.maxDate + 'T23:59:59.999Z');
    // Para validación de mes, usar el primer día del mes
    // Si el primer día del mes es válido, entonces el mes es válido
    const firstDayOfMonth = new Date(year, month - 1, 1);

    if (firstDayOfMonth > maxDateObj) {
      return false;
    }
  }

  return true;
};

/**
 * Formatea valores de día en formato DD/MM/YYYY
 * Retorna string vacío si los valores no son válidos
 */
export const formatDayValue = (
  day?: number,
  month?: number,
  year?: number
): string => {
  if (!day || !month || !year) {
    return '';
  }

  const dayStr = day.toString().padStart(2, '0');
  const monthStr = month.toString().padStart(2, '0');
  const yearStr = year.toString();

  return `${dayStr}/${monthStr}/${yearStr}`;
};

/**
 * Formatea valores de mes en formato MM/YYYY
 * Retorna string vacío si los valores no son válidos
 */
export const formatMonthValue = (
  month?: number,
  year?: number
): string => {
  if (!month || !year) {
    return '';
  }

  const monthStr = month.toString().padStart(2, '0');
  const yearStr = year.toString();

  return `${monthStr}/${yearStr}`;
};

/**
 * Obtiene el año actual
 */
export const getCurrentYear = (): number => {
  return new Date().getFullYear();
};

/**
 * Valida si una cadena de fecha completa es válida
 */
export const isValidDateString = (
  dateString: string,
  granularity: 'day' | 'month',
  options: DateValidationOptions
): boolean => {
  const parsed = parseDateString(dateString);

  if (granularity === 'day') {
    return !!(
      parsed.day &&
      parsed.month &&
      parsed.year &&
      isValidDay(parsed.day, parsed.month, parsed.year, options)
    );
  } else {
    return !!(
      parsed.month &&
      parsed.year &&
      isValidMonth(parsed.month, parsed.year, options)
    );
  }
};

/**
 * Normaliza un valor numérico para input (elimina caracteres no numéricos)
 */
export const normalizeNumericInput = (value: string, maxLength?: number): string => {
  let normalized = value.replace(/\D/g, '');
  
  if (maxLength && normalized.length > maxLength) {
    normalized = normalized.slice(0, maxLength);
  }
  
  return normalized;
};

/**
 * Determina si debe avanzar automáticamente al siguiente campo
 */
export const shouldAutoAdvance = (
  value: string,
  maxLength: number,
  maxValue?: number
): boolean => {
  if (value.length === 0) {
    return false;
  }

  const numValue = parseInt(value, 10);

  if (isNaN(numValue) || numValue === 0) {
    return false;
  }

  if (maxValue && numValue > maxValue) {
    return false;
  }

  // Auto-advance si:
  // 1. El valor tiene el máximo de caracteres, O
  // 2. El valor es válido y no puede ser parte de un número más grande
  if (value.length === maxLength) {
    return true;
  }

  // Para valores de 1 dígito, avanzar si el siguiente dígito haría que exceda el máximo
  if (value.length === 1 && maxValue) {
    const nextMinValue = numValue * 10; // El mínimo valor si agregamos otro dígito
    return nextMinValue > maxValue;
  }

  return false;
};
